
import { supabase } from "@/integrations/supabase/client";
import { UserCategory } from "@/types";
import { offlineDb } from "@/db/offlineDb";
import { syncService } from "@/services/sync.service";
import { networkService } from "@/services/network.service";
import { getCachedUserId, getCachedUser, setCachedUser } from "@/services/auth/sessionCache";
import OfflineLogger from "@/utils/offlineLogger";
import { productRepository } from '@/repositories/product.repository';
import { deleteProductsByCategory } from '@/services/products.service';

export class CategoryOperationsService {
  private async getCurrentUserId(): Promise<string | null> {
    try {
      const cachedId = await getCachedUserId();
      if (cachedId) {
        return cachedId;
      }

      if (networkService.isOnline()) {
        const { data: userData, error } = await supabase.auth.getUser();
        if (!error && userData.user) {
          setCachedUser(userData.user);
          return userData.user.id;
        }
      }

      return null;
    } catch (error) {
      OfflineLogger.error('CategoryOperationsService.getCurrentUserId', error);
      return null;
    }
  }

  async createCategory(categoryData: {
    name: string;
    icon: string;
    color: string;
    text_color: string;
  }): Promise<UserCategory> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error("User not authenticated");
    }

    // Ottieni categorie esistenti per il controllo duplicati e la posizione successiva
    const existingCategories = await offlineDb.categories
      .where('user_id')
      .equals(userId)
      .and(cat => cat._syncStatus !== 'deleted')
      .toArray();

    const normalizedName = categoryData.name.toLowerCase().trim();
    const duplicate = existingCategories.find(cat =>
      cat.name.toLowerCase().trim() === normalizedName
    );

    if (duplicate) {
      return {
        id: duplicate.id,
        name: duplicate.name,
        icon: duplicate.icon,
        color: duplicate.color,
        textColor: duplicate.text_color,
        position: duplicate.position,
        visibile: duplicate.visibile,
        userId: duplicate.user_id,
        createdAt: new Date(duplicate.created_at),
        updatedAt: new Date(duplicate.updated_at)
      };
    }

    const maxPosition = existingCategories.reduce((max, cat) =>
      Math.max(max, cat.position || 0), -1);

    const newCategory = {
      id: crypto.randomUUID(),
      user_id: userId,
      name: categoryData.name,
      icon: categoryData.icon,
      color: categoryData.color,
      text_color: categoryData.text_color,
      position: maxPosition + 1,
      visibile: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const isOnline = networkService.isOnline();

    if (isOnline) {
      try {
        // Crea online
        const { error } = await supabase
          .from("user_categories")
          .insert([newCategory]);

        if (error) throw error;

        // Salva in locale con stato synced
        await offlineDb.categories.put({
          ...newCategory,
          _syncStatus: 'synced' as const,
          _lastModified: Date.now()
        });

        OfflineLogger.info('CategoryOperationsService.createCategory', `Created category ${newCategory.id} online and cached locally`);
      } catch (error) {
        // Se fallisce online, salva in locale per sync successiva
        await offlineDb.categories.put({
          ...newCategory,
          _syncStatus: 'pending' as const,
          _lastModified: Date.now()
        });

        await syncService.addToSyncQueue('categories', 'create', newCategory.id, newCategory);
        OfflineLogger.info('CategoryOperationsService.createCategory', `Failed to create online, queued for sync: ${newCategory.id}`);
      }
    } else {
      // Modalità offline
      await offlineDb.categories.put({
        ...newCategory,
        _syncStatus: 'pending' as const,
        _lastModified: Date.now()
      });

      await syncService.addToSyncQueue('categories', 'create', newCategory.id, newCategory);
      OfflineLogger.info('CategoryOperationsService.createCategory', `Created category ${newCategory.id} offline, queued for sync`);
    }

    return {
      id: newCategory.id,
      name: newCategory.name,
      icon: newCategory.icon,
      color: newCategory.color,
      textColor: newCategory.text_color,
      position: newCategory.position,
      visibile: newCategory.visibile,
      userId: newCategory.user_id,
      createdAt: new Date(newCategory.created_at),
      updatedAt: new Date(newCategory.updated_at)
    };
  }

  async getOrCreateCategory(name: string): Promise<UserCategory> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    const normalized = name.toLowerCase().trim();
    const existing = await offlineDb.categories
      .where('user_id')
      .equals(userId)
      .and(c => c._syncStatus !== 'deleted')
      .toArray();

    const found = existing.find(c => c.name.toLowerCase().trim() === normalized);
    if (found) {
      return {
        id: found.id,
        name: found.name,
        icon: found.icon,
        color: found.color,
        textColor: found.text_color,
        position: found.position,
        visibile: found.visibile,
        userId: found.user_id,
        createdAt: new Date(found.created_at),
        updatedAt: new Date(found.updated_at)
      };
    }

    return await this.createCategory({
      name,
      icon: '📦',
      color: '#f3f4f6',
      text_color: '#000000'
    });
  }

  async updateCategory(categoryId: string, updates: Partial<UserCategory>): Promise<boolean> {
    const isOnline = networkService.isOnline();
    
    const updateData = {
      ...(updates.name !== undefined && { name: updates.name }),
      ...(updates.icon !== undefined && { icon: updates.icon }),
      ...(updates.color !== undefined && { color: updates.color }),
      ...(updates.textColor !== undefined && { text_color: updates.textColor }),
      ...(updates.position !== undefined && { position: updates.position }),
      ...(updates.visibile !== undefined && { visibile: updates.visibile }),
      updated_at: new Date().toISOString()
    };

    // Aggiorna sempre in locale prima
    await offlineDb.categories.update(categoryId, {
      ...updateData,
      _syncStatus: 'pending' as const,
      _lastModified: Date.now()
    });

    if (isOnline) {
      try {
        // Aggiorna online
        const { error } = await supabase
          .from("user_categories")
          .update(updateData)
          .eq("id", categoryId);

        if (error) throw error;

        // Aggiorna stato locale a synced
        await offlineDb.categories.update(categoryId, {
          _syncStatus: 'synced' as const
        });

        OfflineLogger.info('CategoryOperationsService.updateCategory', `Updated category ${categoryId} online and locally`);
        return true;
      } catch (error) {
        // Se fallisce online, rimane in pending per sync successiva
        await syncService.addToSyncQueue('categories', 'update', categoryId, updateData);
        OfflineLogger.info('CategoryOperationsService.updateCategory', `Failed to update online, queued for sync: ${categoryId}`);
        return true;
      }
    } else {
      // Modalità offline
      await syncService.addToSyncQueue('categories', 'update', categoryId, updateData);
      OfflineLogger.info('CategoryOperationsService.updateCategory', `Updated category ${categoryId} offline, queued for sync`);
      return true;
    }
  }

  async deleteCategory(categoryId: string): Promise<boolean> {
    const isOnline = networkService.isOnline();

    if (isOnline) {
      try {
        // Elimina online
        const { error } = await supabase
          .from("user_categories")
          .delete()
          .eq("id", categoryId);

        if (error) throw error;

        // Elimina i prodotti associati online
        await deleteProductsByCategory(categoryId);

        // Rimuovi da locale
        await offlineDb.categories.delete(categoryId);
        await productRepository.deleteByCategory(categoryId, true);

        OfflineLogger.info('CategoryOperationsService.deleteCategory', `Deleted category ${categoryId} and related products online and locally`);
        return true;
      } catch (error) {
        // Se fallisce online, marca per eliminazione
        await offlineDb.categories.update(categoryId, {
          _syncStatus: 'deleted' as const,
          _lastModified: Date.now()
        });
        await productRepository.deleteByCategory(categoryId);

        await syncService.addToSyncQueue('categories', 'delete', categoryId);
        OfflineLogger.info('CategoryOperationsService.deleteCategory', `Failed to delete online, queued for sync: ${categoryId}`);
        return true;
      }
    } else {
      // Modalità offline
      await offlineDb.categories.update(categoryId, {
        _syncStatus: 'deleted' as const,
        _lastModified: Date.now()
      });
      await productRepository.deleteByCategory(categoryId);

      await syncService.addToSyncQueue('categories', 'delete', categoryId);
      OfflineLogger.info('CategoryOperationsService.deleteCategory', `Marked category ${categoryId} for deletion, queued for sync`);
      return true;
    }
  }

  async batchUpdatePositions(categoryUpdates: Array<{ id: string; position: number }>): Promise<boolean> {
    const isOnline = networkService.isOnline();

    // Aggiorna sempre in locale prima
    for (const update of categoryUpdates) {
      await offlineDb.categories.update(update.id, {
        position: update.position,
        updated_at: new Date().toISOString(),
        _syncStatus: 'pending' as const,
        _lastModified: Date.now()
      });
    }

    if (isOnline) {
      try {
        // Aggiorna posizioni online in batch
        for (const update of categoryUpdates) {
          const { error } = await supabase
            .from("user_categories")
            .update({ position: update.position, updated_at: new Date().toISOString() })
            .eq("id", update.id);

          if (error) throw error;
          
          // Aggiorna stato locale a synced
          await offlineDb.categories.update(update.id, {
            _syncStatus: 'synced' as const
          });
        }

        OfflineLogger.info('CategoryOperationsService.batchUpdatePositions', `Updated ${categoryUpdates.length} category positions online and locally`);
        return true;
      } catch (error) {
        // Se fallisce online, salva in locale per sync successiva
        for (const update of categoryUpdates) {
          await syncService.addToSyncQueue('categories', 'update', update.id, {
            position: update.position,
            updated_at: new Date().toISOString()
          });
        }

        OfflineLogger.info('CategoryOperationsService.batchUpdatePositions', `Failed to update positions online, queued for sync`);
        return true;
      }
    } else {
      // Modalità offline
      for (const update of categoryUpdates) {
        await syncService.addToSyncQueue('categories', 'update', update.id, {
          position: update.position,
          updated_at: new Date().toISOString()
        });
      }

      OfflineLogger.info('CategoryOperationsService.batchUpdatePositions', `Updated ${categoryUpdates.length} category positions offline, queued for sync`);
      return true;
    }
  }

  async getPendingOperationsCount(): Promise<number> {
    try {
      const count = await offlineDb.syncQueue
        .where('entityType')
        .equals('categories')
        .count();
      return count;
    } catch (error) {
      OfflineLogger.error('CategoryOperationsService.getPendingOperationsCount', error);
      return 0;
    }
  }
}

export const categoryOperationsService = new CategoryOperationsService();
